import * as React from 'react';
import {I18n, TProvider} from '../../../i18n/i18n';
import {classes, stylesheet} from 'typestyle';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {DiagramInstruction} from '../../../stores/OdtExportStore';
import {tKeyKpi} from '../../../kpi';
import {tSortDirection} from '../../../types/Table';
import {BuildingBlockType, MergedBuildingBlockInstructions, ReportBuildingBlock} from '../../types/Report';
import {WarningColor, ErrorColor, LightGreyColor, WhiteColor, GreyColor} from '../../../styles/colors';
import {tKeyTab} from '../../../stores/store';

interface BuildingBlockMemoryItemContainerProps {
    idParentBlock: string;
    removeDiagramTraitFromBuildingBlock: (idBuildingBlock: string, diagramTrait: DiagramInstruction) => void;
    removeTableTraitFromBuildingBlock: (idBuildingBlock: string, traitKey: string, traitToRemove: any) => void;
    buildingBlock: ReportBuildingBlock;
    mergedBuildingBlockInstructions: MergedBuildingBlockInstructions;
    traitChecks: {
        hasToManyChannels: (reportBlocks: ReportBuildingBlock[]) => boolean;
        hasToManySearches: (reportBlocks: ReportBuildingBlock[]) => boolean;
        hasToManySortings: (reportBlocks: ReportBuildingBlock[]) => boolean;
        reportBuildingBlocksAreEmpty: (buildingBlocks: ReportBuildingBlock[]) => boolean;
        hasSearchProductMixedBuildingBlocks: (reportBlocks: ReportBuildingBlock[]) => boolean;
    };
    removeDiagramCompareTraitFromBuildingBlock: (idBuildingBlock: string) => void;
}

export default class BuildingBlockMemoryItemContainer extends React.Component<BuildingBlockMemoryItemContainerProps> {
    private renderDropArea() {
        return (
            <div className={memoryContainerCss.emptyDropContainer}>
                <div className={memoryContainerCss.emptyDropMessage}>
                    <div className={memoryContainerCss.emptyDropText}>
                        <I18n i18nKey="report:reportEditorActions.dropMemoryBarItemsHere" />
                    </div>
                </div>
            </div>
        );
    }

    private renderMemoryItems(buildingBlocksEmpty: boolean): React.ReactNode[] {
        const {
            removeDiagramTraitFromBuildingBlock,
            idParentBlock,
            removeTableTraitFromBuildingBlock,
            removeDiagramCompareTraitFromBuildingBlock,
        } = this.props;
        const {tableInstruction, diagramInstruction} = this.props.mergedBuildingBlockInstructions;
        let memoryItemsToRender: React.ReactNode[] = [];

        if (diagramInstruction && diagramInstruction.searchDiagrams) {
            const searchDiagramItems = diagramInstruction.searchDiagrams.map((searchDiagram, index) => (
                <MemoryItemTraitContainer
                    useWarningColor={
                        buildingBlocksEmpty || (diagramInstruction.searchDiagrams.length > 0 && diagramInstruction.productDiagrams.length > 0)
                    }
                    key={`diagram${index}`}
                    removeMemoryItemFromBuildingBlock={() => removeDiagramTraitFromBuildingBlock(idParentBlock, searchDiagram)}>
                    <div className={memoryContainerCss.memoryTraitTitle}>
                        <I18n i18nKey="report:traits.searchDiagram" />
                    </div>
                    <div className={memoryContainerCss.traitBody}>
                        {searchDiagram.channel}, {searchDiagram.searchterm ? `${searchDiagram.searchterm},` : null}{' '}
                        <I18n i18nKey={tKeyKpi(searchDiagram.kpi)} />
                    </div>
                </MemoryItemTraitContainer>
            ));
            memoryItemsToRender = [...memoryItemsToRender, ...searchDiagramItems];
        }

        if (diagramInstruction && diagramInstruction.productDiagrams) {
            const productDiagramItems = diagramInstruction.productDiagrams.map((productDiagram, index) => (
                <MemoryItemTraitContainer
                    useWarningColor={
                        buildingBlocksEmpty || (diagramInstruction.productDiagrams.length > 0 && diagramInstruction.searchDiagrams.length > 0)
                    }
                    key={`channelDiagram${index}`}
                    removeMemoryItemFromBuildingBlock={() => removeDiagramTraitFromBuildingBlock(idParentBlock, productDiagram)}>
                    <div className={memoryContainerCss.memoryTraitTitle}>
                        <I18n i18nKey="report:traits.productDiagram" />
                    </div>
                    <div className={memoryContainerCss.traitBody}>
                        {productDiagram.channel}, {productDiagram.productId ? `${productDiagram.productId},` : null}{' '}
                        <I18n i18nKey={tKeyKpi(productDiagram.kpi)} />
                    </div>
                </MemoryItemTraitContainer>
            ));
            memoryItemsToRender = [...memoryItemsToRender, ...productDiagramItems];
        }

        if (diagramInstruction && diagramInstruction.compareRangeActive) {
            const compareRangeActive = (
                <MemoryItemTraitContainer
                    key="compareRangeActive"
                    removeMemoryItemFromBuildingBlock={() => removeDiagramCompareTraitFromBuildingBlock(idParentBlock)}>
                    <div className={memoryContainerCss.memoryTraitTitle}>
                        <I18n i18nKey="report:traits.compareDiagram" />
                    </div>
                    <div className={memoryContainerCss.traitBody}>
                        <I18n i18nKey="active" />
                    </div>
                </MemoryItemTraitContainer>
            );
            memoryItemsToRender = [...memoryItemsToRender, compareRangeActive];
        }

        if (tableInstruction && tableInstruction.activeViews) {
            const activeViewItems = tableInstruction.activeViews.map((activeView, index) => (
                <MemoryItemTraitContainer
                    useWarningColor={buildingBlocksEmpty || tableInstruction.activeViews.length > 1}
                    key={`activeVie${index}`}
                    removeMemoryItemFromBuildingBlock={() => removeTableTraitFromBuildingBlock(idParentBlock, 'analyticsTab', activeView)}>
                    <div className={memoryContainerCss.memoryTraitTitle}>
                        <I18n i18nKey="report:tableType" />
                    </div>
                    <div className={memoryContainerCss.traitBody}>
                        <I18n i18nKey={tKeyTab(activeView)} />
                    </div>
                </MemoryItemTraitContainer>
            ));
            memoryItemsToRender = [...memoryItemsToRender, ...activeViewItems];
        }

        if (tableInstruction && tableInstruction.channels) {
            const channelItems = tableInstruction.channels.map((channel, index) => (
                <MemoryItemTraitContainer
                    useWarningColor={buildingBlocksEmpty || tableInstruction.channels.length > 1}
                    key={`channels${index}`}
                    removeMemoryItemFromBuildingBlock={() => removeTableTraitFromBuildingBlock(idParentBlock, 'channel', channel)}>
                    <div className={memoryContainerCss.memoryTraitTitle}>
                        <I18n count={1} i18nKey="table:channel" />
                    </div>
                    <div className={memoryContainerCss.traitBody}>{channel}</div>
                </MemoryItemTraitContainer>
            ));
            memoryItemsToRender = [...memoryItemsToRender, ...channelItems];
        }

        if (tableInstruction && tableInstruction.filters) {
            const filterItems = tableInstruction.filters.map((filter, index) => (
                <MemoryItemTraitContainer
                    useWarningColor={buildingBlocksEmpty}
                    key={`filters${index}`}
                    removeMemoryItemFromBuildingBlock={() => removeTableTraitFromBuildingBlock(idParentBlock, 'activeFilters', filter)}>
                    <div className={memoryContainerCss.memoryTraitTitle}>
                        <I18n i18nKey="report:traits.filter" />
                    </div>
                    <div className={memoryContainerCss.traitBody}>
                        <I18n i18nKey={tKeyKpi(filter.attribute)} /> {filter.comparator} {filter.value}
                    </div>
                </MemoryItemTraitContainer>
            ));
            memoryItemsToRender = [...memoryItemsToRender, ...filterItems];
        }

        if (tableInstruction && tableInstruction.searches) {
            const searchItems = tableInstruction.searches.map((search, index) => (
                <MemoryItemTraitContainer
                    useWarningColor={buildingBlocksEmpty || tableInstruction.searches.length > 1}
                    key={`searches${index}`}
                    removeMemoryItemFromBuildingBlock={() => removeTableTraitFromBuildingBlock(idParentBlock, 'activeSearch', search)}>
                    <div className={memoryContainerCss.memoryTraitTitle}>
                        <I18n i18nKey="search" />
                    </div>
                    <div className={memoryContainerCss.traitBody}>{search}</div>
                </MemoryItemTraitContainer>
            ));
            memoryItemsToRender = [...memoryItemsToRender, ...searchItems];
        }

        if (tableInstruction && tableInstruction.sortings) {
            const sortingItems = tableInstruction.sortings.map((sorting, index) => (
                <MemoryItemTraitContainer
                    useWarningColor={buildingBlocksEmpty || tableInstruction.sortings.length > 1}
                    key={`sortings${index}`}
                    removeMemoryItemFromBuildingBlock={() => removeTableTraitFromBuildingBlock(idParentBlock, 'activeTableSort', sorting)}>
                    <div className={memoryContainerCss.memoryTraitTitle}>
                        <I18n i18nKey="report:traits.sorting" />
                    </div>
                    <div className={memoryContainerCss.traitBody}>
                        <I18n i18nKey={tKeyKpi(sorting.attribute)} /> <I18n i18nKey={tSortDirection(sorting.sortDirection)} />
                    </div>
                </MemoryItemTraitContainer>
            ));
            memoryItemsToRender = [...memoryItemsToRender, ...sortingItems];
        }
        return memoryItemsToRender;
    }

    private renderWarning(buildingBlocksEmpty: boolean): React.ReactNode[] {
        const {hasToManySortings, hasToManySearches, hasToManyChannels, hasSearchProductMixedBuildingBlocks} = this.props.traitChecks;
        const mixedBlocks = hasSearchProductMixedBuildingBlocks([this.props.buildingBlock]);
        const toManySortings = hasToManySortings([this.props.buildingBlock]);
        const toManySearches = hasToManySearches([this.props.buildingBlock]);
        const toManyChannels = hasToManyChannels([this.props.buildingBlock]);

        const warnings: React.ReactNode[] = [];
        if (buildingBlocksEmpty) {
            warnings.push(<WarningContainer key={0} hoverText={'report:warning.emptyOrMissing'} />);
        }
        if (mixedBlocks) {
            warnings.push(<WarningContainer key={1} hoverText={'report:warning.mixedTraits'} />);
        }

        if (toManySortings) {
            warnings.push(<WarningContainer key={2} hoverText={'report:warning.toManySortings'} />);
        }
        if (toManySearches) {
            warnings.push(<WarningContainer key={3} hoverText={'report:warning.toManySearches'} />);
        }
        if (toManyChannels) {
            warnings.push(<WarningContainer key={4} hoverText={'report:warning.toManyChannels'} />);
        }
        return warnings;
    }

    public render() {
        const {buildingBlock} = this.props;
        const {blockType} = buildingBlock;
        const {reportBuildingBlocksAreEmpty} = this.props.traitChecks;
        if (blockType === BuildingBlockType.TABLE || blockType === BuildingBlockType.DIAGRAM || blockType === BuildingBlockType.DIAGRAM_TABLE) {
            const buildingBlocksEmpty = reportBuildingBlocksAreEmpty([this.props.buildingBlock]);

            const memoryItemsToRender = this.renderMemoryItems(buildingBlocksEmpty);
            return (
                <div className={memoryContainerCss.tableBlockContainer}>
                    {memoryItemsToRender.length > 0 ? (
                        <div className={memoryContainerCss.memoryItemContainer}>
                            {this.renderWarning(buildingBlocksEmpty)}
                            {memoryItemsToRender}
                        </div>
                    ) : (
                        this.renderDropArea()
                    )}
                </div>
            );
        } else {
            throw Error(
                'BuildingBlockMemoryItemContainer can only be used for diagram, table and diagramTable building blocks, tried for ' +
                    buildingBlock.blockType
            );
        }
    }
}

interface MemoryItemTraitProps {
    removeMemoryItemFromBuildingBlock: () => void;
    useWarningColor?: boolean;
    children?: React.ReactNode;
}

function WarningContainer({hoverText}: {hoverText: string}) {
    return (
        <TProvider>
            {(t) => (
                <div className={classes(memoryContainerCss.memoryRow, 'error')} title={t(hoverText)}>
                    <FontAwesomeIcon icon="exclamation-triangle" />
                </div>
            )}
        </TProvider>
    );
}

function MemoryItemTraitContainer({removeMemoryItemFromBuildingBlock, useWarningColor, children}: MemoryItemTraitProps) {
    return (
        <div className={classes(memoryContainerCss.memoryRow, useWarningColor ? 'warning' : undefined)}>
            <div>{children}</div>
            <div className={memoryContainerCss.removeMemoryRow} onClick={removeMemoryItemFromBuildingBlock}>
                <FontAwesomeIcon icon="times" />
            </div>
        </div>
    );
}

const memoryContainerCss = stylesheet({
    traitBody: {
        paddingRight: 10,
    },
    memoryTraitTitle: {
        fontSize: 12,
        fontWeight: 'bold',
    },
    emptyDropText: {
        color: GreyColor,
    },
    emptyDropMessage: {
        flexGrow: 1,
        borderRadius: 2,
        position: 'relative',
        padding: 15,
    },
    emptyDropContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
    },
    memoryRow: {
        backgroundColor: 'transparent',
        textAlign: 'left',
        display: 'flex',
        border: '1px solid ' + LightGreyColor,
        borderRadius: 2,
        marginRight: 5,
        marginBottom: 5,
        padding: 5,
        $nest: {
            '&.warning': {
                backgroundColor: WarningColor,
            },
            '&.error': {
                backgroundColor: ErrorColor,
                color: WhiteColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: '.2s',
            },
        },
    },
    removeMemoryRow: {
        cursor: 'pointer',
        width: 20,
        height: 20,
        color: LightGreyColor,
        marginTop: -5,
        marginRight: -5,
        textAlign: 'center',
    },
    tableBlockContainer: {
        position: 'relative',
        transition: '.2s',
        padding: 5,
        paddingBottom: 0,
        display: 'flex',
        flexDirection: 'column',
    },
    memoryItemContainer: {
        width: '100%',
        display: 'flex',
        flexWrap: 'wrap',
    },
});



// WEBPACK FOOTER //
// ./src/report/views/PaperSheetBlocks/BuildingBlockMemoryItemContainer.tsx